import { useState } from 'react'
import { Button } from './Button.js'
import { Card } from './Card.js'
import { CloseIcon, StarFilledIcon, StarIcon } from './icons.js'

// ---------------------------------------------------------------------------
// Persistence helpers — 30-day cooldown stored in localStorage so the prompt
// doesn't reappear after the user has already given feedback recently.
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'ielts-buddy:feedback:last_given'
const COOLDOWN_DAYS = 30

export function shouldShowFeedback(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return true
    const daysSince = (Date.now() - Number(raw)) / (1000 * 60 * 60 * 24)
    return daysSince >= COOLDOWN_DAYS
  } catch {
    return true
  }
}

function markFeedbackGiven(): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()))
  } catch {
    // localStorage unavailable (private browsing etc.) — silently ignore
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface FeedbackPromptProps {
  /** Called when the user submits a rating. Parent handles the API call. */
  onSubmit: (params: { rating: number; comment?: string }) => Promise<void>
  /** Called when the user dismisses without rating. */
  onDismiss?: () => void
}

export function FeedbackPrompt({ onSubmit, onDismiss }: FeedbackPromptProps) {
  const [hovered, setHovered] = useState(0)
  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  const activeLevel = hovered || rating

  // Only ask a follow-up for clear signal (not for neutral 3)
  const followUpLabel =
    rating > 0 && rating <= 2
      ? 'What could be better?'
      : 'What did you like most?'
  const showFollowUp = rating > 0 && rating !== 3

  async function handleSubmit() {
    if (!rating || submitting) return
    setSubmitting(true)
    try {
      await onSubmit({ rating, comment: comment.trim() || undefined })
      markFeedbackGiven()
      setDone(true)
    } finally {
      setSubmitting(false)
    }
  }

  function handleDismiss() {
    markFeedbackGiven()
    onDismiss?.()
  }

  if (done) {
    return (
      <Card tone="primary-soft" className="p-4 text-center animate-fade-in">
        <div className="text-sm font-semibold text-brand-primary">
          Thanks for the feedback!
        </div>
        <div className="text-xs text-brand-text-muted mt-0.5">
          It helps us improve the experience.
        </div>
      </Card>
    )
  }

  return (
    <Card className="p-4 animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-wider text-brand-text-muted font-semibold">
          How was this session?
        </span>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="text-brand-text-subtle active:opacity-50 -mr-1"
        >
          <CloseIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Star rating */}
      <div
        className="flex gap-1 mb-4"
        onMouseLeave={() => setHovered(0)}
        role="group"
        aria-label="Rate this session from 1 to 5 stars"
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            aria-label={`${n} star${n !== 1 ? 's' : ''}`}
            aria-pressed={rating === n}
            onMouseEnter={() => setHovered(n)}
            onClick={() => setRating(n)}
            className="flex-1 flex justify-center py-1 active:scale-90 transition-transform"
          >
            {n <= activeLevel ? (
              <StarFilledIcon className="w-7 h-7 text-brand-primary" />
            ) : (
              <StarIcon className="w-7 h-7 text-brand-text-subtle" />
            )}
          </button>
        ))}
      </div>

      {/* Conditional follow-up */}
      {showFollowUp && (
        <div className="mb-3 animate-fade-in">
          <label className="text-xs text-brand-text-muted mb-1.5 block">
            {followUpLabel}
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="We'd love to hear the details — even one sentence helps"
            className="w-full text-sm bg-brand-bg-soft rounded-lg px-3 py-2 text-brand-text placeholder:text-brand-text-subtle resize-none focus:outline-none focus:ring-1 focus:ring-brand-primary/40 border border-brand-border"
          />
        </div>
      )}

      {rating > 0 && (
        <Button
          size="sm"
          className="w-full"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Sending…' : 'Send feedback'}
        </Button>
      )}
    </Card>
  )
}
